import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { slotCounterOptions } from '@models/system-setting';
import { firstValueFrom, lastValueFrom, take } from 'rxjs';
import { SlotDialogComponent } from '../slot-dialog/slot-dialog.component';
import { emptySlot, Slot, slotOptions, SlotOptionType } from 'icreate/common/models/slot';
import { ValidationError, ValidationErrorMap } from 'icreate/common/validators';
import { CommunicationType, communicationTypes } from 'icreate/common/models/fu-unit';
import { TranslateService } from '@ngx-translate/core';
import { MessageDialogService } from '@service/message-dialog.service';
import { OpsSettingsConfirmDialogComponent } from '@src/app/pages/ops-settings/ops-settings-confirmation-dialog/ops-settings-confirm-dialog.component';
import { SlotDisplayService } from '@helpers/slot-display-utils';

export enum FuInputName {
    communicationType = 'communicationType',
    fieldNetLineAIp = 'fieldNetLineAIp',
    fieldNetLineBIp = 'fieldNetLineBIp',
    location = 'location',
    slotCount = 'slotCount',
}

@Component({
    selector: 'app-fu-setting-input-container',
    templateUrl: './fu-setting-input-container.component.html',
    styleUrls: ['./fu-setting-input-container.component.css'],
    standalone: false,
})
export class FuSettingInputContainerComponent {
    @ViewChild('moreOptions') moreOptions: ElementRef;
    @ViewChild('errorPopup') errorPopup!: ElementRef;

    readonly COMMUNICATION_TYPE = FuInputName.communicationType;
    readonly FIELD_NET_A_IP = FuInputName.fieldNetLineAIp;
    readonly FIELD_NET_B_IP = FuInputName.fieldNetLineBIp;

    readonly LOCATION = FuInputName.location;
    readonly SLOT_COUNT = FuInputName.slotCount;

    readonly SERIAL = communicationTypes.serial;
    readonly LAN = communicationTypes.lan;

    readonly FU_SLOT_COUNTER_OPTIONS = slotCounterOptions;
    readonly FU_SLOT_OPTIONS = slotOptions;
    readonly EMPTY_SLOT = emptySlot;

    @Input() index!: number;
    @Input() selectedCommunicationType!: any;
    @Input() fieldNetLineAIp!: string;
    @Input() fieldNetLineBIp!: string;
    @Input() slotList: Slot[] = [];
    @Input() vesselId: string = '';
    @Input() location: string = '';
    @Input() validationErrors: { [propertyName: string]: ValidationError | { message: string } } = {};

    @Input() canCopy: boolean = true;

    selectedSlotCounter: number | string = undefined;
    isOpenOption: boolean = false;
    errorMessageVisible: boolean = false;
    confirmationDialogLabel: string = '';

    @Output() onChangeFuData = new EventEmitter<{
        vesselId: string;
        index: number;
        communicationType: CommunicationType;
        fieldNetLineAIp: string;
        fieldNetLineBIp: string;
        slotList: Slot[];
        location: string;
    }>();
    @Output() onCopyFuData = new EventEmitter<{
        id: string;
        vesselId: string;
        index: number;
        communicationType: CommunicationType;
        fieldNetLineAIp: string;
        fieldNetLineBIp: string;
        slotList: Slot[];
        location: string;
        validationErrors: ValidationErrorMap;
    }>();
    @Output() onDeleteFuData = new EventEmitter<number>();
    @Output() onClearFuData = new EventEmitter<number>();

    constructor(
        private dialog: MatDialog,
        private translateService: TranslateService,
        private messageDialogService: MessageDialogService,
        private slotDisplayService: SlotDisplayService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (this.isOpenOption && !this.moreOptions.nativeElement.contains(event.target)) {
            this.isOpenOption = false;
        }

        if (this.errorMessageVisible && !this.errorPopup?.nativeElement.contains(event.target)) {
            this.errorMessageVisible = false;
        }
    }

    async ngOnInit() {
        this.setDefaultSlotCounter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.slotList) {
            this.setDefaultSlotCounter();
        }
    }

    setDefaultSlotCounter() {
        const slotCounter = this.slotList.filter(slot => slot.enable).length;
        this.selectedSlotCounter = slotCounter === 0 ? '' : slotCounter;
    }

    inputChange(event: { name: string; value: string }) {
        const { name, value } = event;
        switch (name) {
            case this.FIELD_NET_A_IP:
                this.fieldNetLineAIp = value;
                break;
            case this.FIELD_NET_B_IP:
                this.fieldNetLineBIp = value;
                break;
            case this.LOCATION:
                this.location = value;
                break;
        }

        this.onChangeFuData.emit({
            vesselId: this.vesselId,
            index: this.index,
            communicationType: this.selectedCommunicationType,
            fieldNetLineAIp: this.fieldNetLineAIp,
            fieldNetLineBIp: this.fieldNetLineBIp,
            slotList: this.slotList,
            location: this.location,
        });
    }

    selectInput(event: { name: string; value: string | number }) {
        const { name, value } = event;

        if (name === this.SLOT_COUNT) {
            this.selectedSlotCounter = Number(value);
            this.slotList = this.slotList.map((slot, index) => ({
                ...slot,
                ioType: index <= Number(this.selectedSlotCounter) - 1 ? slot.ioType : '',
                enable: index <= Number(this.selectedSlotCounter) - 1,
            }));
        } else if (name === this.COMMUNICATION_TYPE) {
            this.selectedCommunicationType = String(value);

            if (this.selectedCommunicationType === communicationTypes.serial) {
                this.fieldNetLineAIp = '';
                this.fieldNetLineBIp = '';
            }
        }

        this.onChangeFuData.emit({
            vesselId: this.vesselId,
            index: this.index,
            communicationType: this.selectedCommunicationType,
            fieldNetLineAIp: this.fieldNetLineAIp,
            fieldNetLineBIp: this.fieldNetLineBIp,
            slotList: this.slotList,
            location: this.location,
        });
    }

    async selectSlotOption(event: { name: string; value: SlotOptionType; slotId?: string | null }) {
        const { name, value, slotId = null } = event;
        let slotIndex: number;

        if (slotId) {
            slotIndex = this.slotList.findIndex(slot => slot.id === slotId);
        } else {
            const match = name.match(/\d+$/);
            const lastNumber = match ? Number(match[0]) : null;
            slotIndex = lastNumber !== null ? lastNumber - 1 : -1;
        }

        const slot = slotIndex >= 0 ? this.slotList[slotIndex] : null;
        const isEdit = !!slot?.terminal?.length;
        const isSameIoType = slot?.ioType === value;

        if (isEdit && !isSameIoType) {
            const confirmationDialogLabel = this.translateService.instant('system-settings.slot.change-confirmation', {
                oldSlot: slot?.ioType,
                newSlot: value,
            });

            const dialogRef = this.dialog.open(OpsSettingsConfirmDialogComponent, {
                width: '474px',
                data: {
                    title: confirmationDialogLabel,
                    description: 'system-settings.slot.change.sub-message',
                    cancelText: 'system-settings.slot.change.cancel',
                    confirmText: 'system-settings.slot.change.confirm',
                    isDelete: true,
                },
            });

            const result = await lastValueFrom(dialogRef.afterClosed());

            if (!result) return;

            this.slotList[slotIndex] = {
                ...this.slotList[slotIndex],
                ioType: emptySlot,
                terminal: [],
                enable: false,
            };

            this.onChangeFuData.emit({
                vesselId: this.vesselId,
                index: this.index,
                communicationType: this.selectedCommunicationType,
                fieldNetLineAIp: this.fieldNetLineAIp,
                fieldNetLineBIp: this.fieldNetLineBIp,
                slotList: this.slotList,
                location: this.location,
            });
        }

        const slotDialogRef = this.dialog.open(SlotDialogComponent, {
            data: {
                title: `FU ${this.index}`,
                ioType: value,
                terminal: isEdit && isSameIoType ? slot!.terminal : [],
            },
            disableClose: true,
        });

        const dialogResult = await firstValueFrom(slotDialogRef.afterClosed());

        if (!dialogResult) {
            return;
        }

        const newSlot: Slot = {
            ...slot,
            ...dialogResult,
            ioType: value,
            enable: true,
        };

        this.slotList.splice(slotIndex, 1, newSlot);

        this.onChangeFuData.emit({
            vesselId: this.vesselId,
            index: this.index,
            communicationType: this.selectedCommunicationType,
            fieldNetLineAIp: this.fieldNetLineAIp,
            fieldNetLineBIp: this.fieldNetLineBIp,
            slotList: this.slotList,
            location: this.location,
        });

        this.messageDialogService.openMessage('system-settings.slot.saved.notification', { slot: value }, false, {
            bottom: '96px',
        });
    }

    openMoreOption() {
        this.isOpenOption = !this.isOpenOption;
    }

    copy() {
        this.onCopyFuData.emit({
            id: '',
            vesselId: this.vesselId,
            index: this.index,
            communicationType: this.selectedCommunicationType,
            fieldNetLineAIp: this.fieldNetLineAIp,
            fieldNetLineBIp: this.fieldNetLineBIp,
            slotList: this.slotList,
            location: this.location,
            validationErrors: this.validationErrors,
        });

        this.isOpenOption = false;
    }

    clear() {
        this.onClearFuData.emit(this.index);
        this.selectedSlotCounter = '';
        this.isOpenOption = false;
    }

    deleteFuData() {
        this.onDeleteFuData.emit(this.index);
        this.isOpenOption = false;
    }

    toggleError() {
        this.errorMessageVisible = !this.errorMessageVisible;
    }

    getIPErrors() {
        if (this.validationErrors) {
            const count = Object.values(this.validationErrors).filter((e: any) =>
                e?.errors?.includes('errors.validation.generic.ip-address-not-valid')
            ).length;
            return count;
        } else return 0;
    }

    getSlotValue(slot) {
        return this.slotDisplayService.getSlotValue(slot);
    }

    getEmptySlotLabel(slotNum) {
        const translationString = this.translateService.instant('system-settings.fcu.label.slot-empty');

        return translationString + ' ' + slotNum;
    }
}
